/**
 * Analytics utility - lightweight event tracking for landing page
 *
 * v3.94 - X-03: Landing page event tracking - 2026-03-20
 *
 * Thin wrapper over window.gtag. If gtag is not loaded, events are silently dropped.
 * Logs to console in development for visibility.
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

type CTALocation = 'hero' | 'nav' | 'final_cta';
type SocialProofVariant = 'quote' | 'counter' | 'logos';
type ScrollMilestone = 25 | 50 | 75 | 100;

function track(event: string, params: Record<string, unknown> = {}): void {
  if (typeof window.gtag === 'function') {
    window.gtag('event', event, params);
  }
  if (process.env.NODE_ENV === 'development') {
    console.log(`[analytics] ${event}`, params);
  }
}

export const analytics = {
  /** Primary CTA button clicked — hero, nav, or final CTA block */
  ctaClick: (location: CTALocation, siteMode: string): void =>
    track('cta_click', { location, site_mode: siteMode }),

  /** "Have an invite code?" secondary link clicked (pilot mode only) */
  inviteCodeClick: (): void =>
    track('invite_code_click', { site_mode: 'pilot' }),

  /** Pricing section scrolled into view (only fires when features.pricing=true) */
  pricingSectionView: (): void =>
    track('pricing_section_view', { feature_pricing: true }),

  /** Waitlist form submitted */
  waitlistSubmit: (siteMode: string): void =>
    track('waitlist_submit', { site_mode: siteMode }),

  /** About nav or footer link clicked */
  aboutClick: (): void =>
    track('about_click'),

  /** Page scroll depth milestone reached */
  scrollDepth: (depth: ScrollMilestone): void =>
    track('scroll_depth', { depth }),

  /** Social proof section scrolled into view */
  socialProofView: (variant: SocialProofVariant): void =>
    track('social_proof_view', { variant }),
};
